
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import {authAPI} from "../services/api";

interface AuthContextType {
    user: User | null;
    token: string | null;
    refreshToken: string | null;
    login: (user: User, token: string, refreshToken: string) => void;
    logout: () => void;
    updateUser: (user: User) => void;
    isAuthenticated: boolean;
    isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const logout = () => {
        setUser(null);
        setToken(null);
        setRefreshToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('theme');
        window.location.href = '/login';
    };

    useEffect(() => {
        const handleTokenRefresh = (event: CustomEvent) => {
            const { accessToken, refreshToken: newRefreshToken } = event.detail;
            setToken(accessToken);
            setRefreshToken(newRefreshToken);
        };

        window.addEventListener('token-refreshed', handleTokenRefresh as EventListener);

        return () => {
            window.removeEventListener('token-refreshed', handleTokenRefresh as EventListener);
        };
    }, []);

    useEffect(() => {
        const initAuth = async () => {
            try {
                const storedToken = localStorage.getItem('accessToken');
                const storedRefreshToken = localStorage.getItem('refreshToken');

                if (storedToken && storedRefreshToken) {
                    setToken(storedToken);
                    setRefreshToken(storedRefreshToken);

                    const res = await authAPI.getProfile();

                    if (res?.user) {
                        setUser({
                            ...res.user
                        });
                        localStorage.setItem('user', JSON.stringify(res.user));
                    } else {
                        logout();
                    }
                }
            } catch (error) {
                console.error('Error initializing auth:', error);
                logout();
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = (userData: User, tokenData: string, refreshToken: string) => {
        setUser(userData);
        setToken(tokenData);
        setRefreshToken(refreshToken);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('accessToken', tokenData);
        localStorage.setItem('refreshToken', refreshToken);
    };

    const updateUser = (userData: User) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
    };

    const value = {
        user,
        token,
        refreshToken,
        login,
        logout,
        updateUser,
        isAuthenticated: !!user && !!token,
        isLoading,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};